import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/auth/AuthContext";
import { fmtPhone } from "@/lib/format";
import { fetchUserPackageStatus } from "@/lib/api";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ReferralDialog } from "./ReferralDialog";
import { ResetPasswordModal } from "../components/ui/ResetPasswordModal";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  UserCircle,
  SignOut,
  Heart,
  SignIn,
  UserPlus,
  Wallet,
  Link as LinkIcon,
  Lock,
  WarningCircle,
} from "@phosphor-icons/react";

const LOGO_URL =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_estate-view-9/artifacts/1rfj1qxl_output-onlinepngtools.png";

export const Header = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [refOpen, setRefOpen] = useState(false);
  const [resetPassOpen, setResetPassOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const [packageExpiredModalOpen, setPackageExpiredModalOpen] = useState(false);
  const [packageMessage, setPackageMessage] = useState("Aktiv paketiniz yoxdur");

  const token = localStorage.getItem("emlak_access_token");

  useEffect(() => {
    if (user && token) {
      fetchUserPackageStatus()
        .then((data) => {
          const isDetailPath =
            location.pathname.includes("/detail") ||
            location.pathname.includes("/property");

          if (data && data.isActive === false) {
            localStorage.setItem("emlak_has_package", "false");
            if (!isDetailPath) {
              setPackageExpiredModalOpen(true);
            }
          } else {
            localStorage.setItem("emlak_has_package", "true");
          }
        })
        .catch((err) => {
          const isDetailPath =
            location.pathname.includes("/detail") ||
            location.pathname.includes("/property");

          localStorage.setItem("emlak_has_package", "false");
          if (err?.message) {
            setPackageMessage(err.message);
          }
          if (!isDetailPath) {
            setPackageExpiredModalOpen(true);
          }
        });
    }
  }, [user, token, location.pathname]);

  const handleConfirmLogout = () => {
    localStorage.removeItem("emlak_has_package");
    logout();
    window.location.reload();
    setLogoutConfirmOpen(false);
    nav("/");
  };

  const handleGoToPayments = () => {
    setPackageExpiredModalOpen(false);
    nav("/payments?autoOpen=true");
  };

  const handleNavigateFavorites = () => {
    const hasPackage = localStorage.getItem("emlak_has_package");
    if (hasPackage === "false") {
      setPackageExpiredModalOpen(true);
    } else {
      nav("/favorites");
    }
  };

  return (
    <header className="sticky top-0 z-40 glass border-b border-border/50">
      <div className="w-full px-4 lg:px-8 h-14 flex items-center gap-3">
        <Link
          to="/"
          data-testid="logo-link"
          className="flex items-center gap-2 group shrink-0"
        >
          <img
            src={LOGO_URL}
            alt="Emlak PRO"
            className="h-9 w-9 object-contain transition-transform group-hover:scale-105"
          />
          <span className="font-display text-lg font-bold tracking-tight hidden sm:inline">
            <span className="text-brand-teal">Əmlak</span>{" "}
            <span className="text-brand-blue">PRO</span>
          </span>
        </Link>

        <div className="ml-auto flex items-center gap-2">
          <LanguageSwitcher />

          {!user && (
            <>
              <Button
                variant="ghost"
                size="sm"
                data-testid="header-login-btn"
                onClick={() => nav("/login")}
                className="h-9  sm:inline-flex gap-1.5"
              >
                <SignIn size={14} weight="bold" /> Daxil ol
              </Button>
              <Button
                size="sm"
                data-testid="header-register-btn"
                onClick={() => nav("/register")}
                className="h-9 hidden sm:inline-flex rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5"
              >
                <UserPlus size={14} weight="bold" /> Qeydiyyat
              </Button>
            </>
          )}

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="profile-menu-btn"
                  className="w-10 h-10 rounded-full bg-primary text-primary-foreground font-semibold flex items-center justify-center shadow-sm hover:bg-primary/90 transition"
                  aria-label="Profile"
                >
                  <UserCircle size={20} weight="fill" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col gap-0.5">
                    <div
                      className="font-semibold text-sm truncate"
                      data-testid="profile-name"
                    >
                      {fmtPhone(user.phoneNumber)}
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />

                {token && (
                  <DropdownMenuItem
                    onClick={handleNavigateFavorites}
                    data-testid="menu-favorites"
                    className="cursor-pointer"
                  >
                    <Heart size={14} weight="duotone" className="mr-2" />{" "}
                    Seçdiklərim
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem
                  onClick={() => nav("/payments")}
                  data-testid="menu-payments"
                  className="cursor-pointer"
                >
                  <Wallet size={14} weight="duotone" className="mr-2" />{" "}
                  Ödənişlər
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => setRefOpen(true)}
                  data-testid="menu-referrals"
                  className="cursor-pointer"
                >
                  <LinkIcon size={14} weight="bold" className="mr-2" />{" "}
                  Referanslar
                </DropdownMenuItem>

                <DropdownMenuItem
                  onClick={() => setResetPassOpen(true)}
                  data-testid="menu-reset-password"
                  className="cursor-pointer"
                >
                  <Lock size={14} weight="bold" className="mr-2" /> Şifrəni
                  dəyişdir
                </DropdownMenuItem>

                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setLogoutConfirmOpen(true);
                  }}
                  data-testid="menu-logout"
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <SignOut size={14} weight="bold" className="mr-2" /> Çıxış et
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="anon-menu-btn"
                  className="w-10 h-10 rounded-full border border-border bg-card hover:border-primary hover:text-primary transition-colors flex items-center justify-center text-muted-foreground"
                  aria-label="Profile menu"
                >
                  <UserCircle size={22} weight="duotone" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Hesab</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => nav("/login")}
                  data-testid="menu-login"
                  className="cursor-pointer"
                >
                  <SignIn size={14} weight="bold" className="mr-2" /> Daxil ol
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => nav("/register")}
                  data-testid="menu-register"
                  className="cursor-pointer"
                >
                  <UserPlus size={14} weight="bold" className="mr-2" /> Qeydiyyat
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <ReferralDialog open={refOpen} onOpenChange={setRefOpen} />
      <ResetPasswordModal
        isOpen={resetPassOpen}
        onClose={() => setResetPassOpen(false)}
      />

      <AlertDialog
        open={logoutConfirmOpen}
        onOpenChange={setLogoutConfirmOpen}
      >
        <AlertDialogContent className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-w-md w-full">
          <AlertDialogHeader>
            <AlertDialogTitle>Sistemdən çıxış edilsinmi?</AlertDialogTitle>
            <AlertDialogDescription>
              Hesabınızdan çıxış edəcəksiniz və yenidən daxil olmalı
              olacaqsınız.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İmtina</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLogout}>
              Davam et
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
};